import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  IMAGE_GENERATION_MODELS, 
  SCHEDULERS
} from '@/lib/utils/constants/imageGeneration';

interface TechnicalParams {
  model: string;
  scheduler: string;
  guidanceScale: number;
  inferenceSteps: number;
  seed: number;
}

interface TechnicalParamsEditorProps {
  params: TechnicalParams;
  onParamsChange: (params: TechnicalParams) => void;
}

type ParameterBase = {
  label: string;
  min: number;
  max: number;
  default: number;
  step?: number;
}

type ParameterWithDescriptions = ParameterBase & {
  descriptions: Record<number, string>;
}

type ParameterWithDescription = ParameterBase & {
  description: string;
}

type Parameter = ParameterWithDescriptions | ParameterWithDescription;

const PARAMETERS: Record<string, Parameter> = {
  guidanceScale: {
    label: "Guidance Scale",
    min: 1,
    max: 20,
    step: 0.1,
    default: 7.5,
    descriptions: {
      1: "Subtle",
      5: "Balanced",
      10: "Strong",
      15: "Very Strong",
      20: "Extreme"
    }
  },
  inferenceSteps: {
    label: "Inference Steps",
    min: 20,
    max: 150,
    step: 1,
    default: 50,
    descriptions: {
      20: "Fast",
      50: "Balanced",
      100: "Detailed",
      150: "Ultra Detailed"
    }
  },
  seed: {
    label: "Seed",
    min: -1,
    max: 2147483647,
    default: -1,
    description: "-1 for random magic ✨"
  }
};

export function TechnicalParamsEditor({
  params,
  onParamsChange,
}: TechnicalParamsEditorProps) {
  const getDescriptionForValue = (
    parameter: keyof typeof PARAMETERS,
    value: number
  ): string => {
    const param = PARAMETERS[parameter];
    
    if ('description' in param) {
      return param.description;
    }
    
    if ('descriptions' in param) {
      const descriptions = param.descriptions;
      if (!descriptions) return value.toString();
      
      const levels = Object.entries(descriptions)
        .map(([level, desc]) => ({ level: Number(level), desc }))
        .sort((a, b) => a.level - b.level);

      const closest = levels.reduce((prev, curr) => {
        return Math.abs(curr.level - value) < Math.abs(prev.level - value) ? curr : prev;
      });

      return `${closest.desc} (${value})`;
    }
    
    return value.toString();
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label>Model</Label>
        <Select
          value={params.model}
          onValueChange={(value) => onParamsChange({ ...params, model: value })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(IMAGE_GENERATION_MODELS).map(([key, model]) => (
              <SelectItem key={key} value={key}>
                {model}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Scheduler</Label>
        <Select
          value={params.scheduler}
          onValueChange={(value) => onParamsChange({ ...params, scheduler: value })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(SCHEDULERS).map(([key, scheduler]) => (
              <SelectItem key={key} value={key}>
                {scheduler}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between">
          <Label>{PARAMETERS.guidanceScale.label}</Label>
          <span className="text-sm text-muted-foreground">
            {getDescriptionForValue('guidanceScale', params.guidanceScale)}
          </span>
        </div>
        <Slider
          value={[params.guidanceScale]}
          min={PARAMETERS.guidanceScale.min}
          max={PARAMETERS.guidanceScale.max}
          step={PARAMETERS.guidanceScale.step}
          onValueChange={([value]) => onParamsChange({ ...params, guidanceScale: value })}
        />
      </div>

      <div className="space-y-2">
        <div className="flex justify-between">
          <Label>{PARAMETERS.inferenceSteps.label}</Label>
          <span className="text-sm text-muted-foreground">
            {getDescriptionForValue('inferenceSteps', params.inferenceSteps)}
          </span>
        </div>
        <Slider
          value={[params.inferenceSteps]}
          min={PARAMETERS.inferenceSteps.min}
          max={PARAMETERS.inferenceSteps.max}
          step={PARAMETERS.inferenceSteps.step}
          onValueChange={([value]) => onParamsChange({ ...params, inferenceSteps: value })}
        />
      </div>

      <div className="space-y-2">
        <div className="flex justify-between">
          <Label>{PARAMETERS.seed.label}</Label>
          <span className="text-sm text-muted-foreground">
            {params.seed === -1 && 'description' in PARAMETERS.seed 
              ? PARAMETERS.seed.description 
              : params.seed}
          </span>
        </div>
        <Input
          type="number"
          value={params.seed}
          onChange={(e) => onParamsChange({ 
            ...params, 
            seed: Math.max(
              PARAMETERS.seed.min, 
              Math.min(PARAMETERS.seed.max, Number(e.target.value))
            ) 
          })}
          min={PARAMETERS.seed.min}
          max={PARAMETERS.seed.max}
        />
      </div>
    </div>
  );
} 